import type { LngLat } from "@/utils/polyline";

/**
 * Device-local saved places ("Home", "Work", "Mom's house", etc.).
 * Stored in localStorage. Cloud sync can be layered on later without
 * changing the calling sites — keep the surface tiny.
 */
export interface SavedPlace {
  id: string;
  /** Short user-facing label, e.g. "Home", "Work", "Mom". */
  label: string;
  /** Optional emoji/icon — defaults are picked by `iconFor` below. */
  icon?: string;
  /** Original geocoder name (street / business). */
  name: string;
  /** Full geocoder address. */
  place: string;
  coords: LngLat;
  createdAt: number;
}

const KEY = "pleasant.savedPlaces.v1";

function read(): SavedPlace[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is SavedPlace =>
        p && typeof p.id === "string" && typeof p.label === "string" &&
        Array.isArray(p.coords) && p.coords.length === 2,
    );
  } catch {
    return [];
  }
}

function write(places: SavedPlace[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(places));
  } catch {
    /* quota / private mode — silently ignore */
  }
  window.dispatchEvent(new CustomEvent("saved-places-changed"));
}

export function loadSavedPlaces(): SavedPlace[] {
  return read();
}

export function saveSavedPlace(p: Omit<SavedPlace, "id" | "createdAt">): SavedPlace {
  const all = read();
  // De-dupe by label (case-insensitive) — saving "Home" twice replaces.
  const filtered = all.filter((x) => x.label.trim().toLowerCase() !== p.label.trim().toLowerCase());
  const next: SavedPlace = {
    ...p,
    label: p.label.trim().slice(0, 40),
    id: crypto.randomUUID(),
    createdAt: Date.now(),
  };
  filtered.unshift(next);
  // Cap at 24 to keep the chip row sane.
  write(filtered.slice(0, 24));
  return next;
}

export function updateSavedPlace(
  id: string,
  patch: Partial<Pick<SavedPlace, "label" | "icon" | "name" | "place" | "coords">>,
): void {
  const all = read();
  const next = all.map((p) =>
    p.id === id
      ? {
          ...p,
          ...(patch.label !== undefined ? { label: patch.label.trim().slice(0, 40) } : {}),
          ...(patch.icon !== undefined ? { icon: patch.icon } : {}),
          ...(patch.name !== undefined ? { name: patch.name } : {}),
          ...(patch.place !== undefined ? { place: patch.place } : {}),
          ...(patch.coords !== undefined ? { coords: patch.coords } : {}),
        }
      : p,
  );
  write(next);
}

export function deleteSavedPlace(id: string): void {
  write(read().filter((p) => p.id !== id));
}

/**
 * Find a saved place at (or very near) the given coords.
 * Tolerance default ~30m — enough to absorb geocoder rounding but tight
 * enough that two distinct addresses don't collide.
 */
export function findSavedPlaceNear(
  coords: LngLat,
  toleranceM = 30,
): SavedPlace | undefined {
  const [lng, lat] = coords;
  for (const p of read()) {
    const dLat = (p.coords[1] - lat) * 111_000;
    const dLng = (p.coords[0] - lng) * 111_000 * Math.cos((lat * Math.PI) / 180);
    if (Math.hypot(dLat, dLng) < toleranceM) return p;
  }
  return undefined;
}

/**
 * Locate the user's Home place. Matches the same label heuristic as
 * `iconFor` so any label containing "home" / "house" / "apartment" / "apt"
 * counts — that makes the lookup forgiving for users who label theirs
 * "Home sweet home" or "My apartment" rather than the exact word "Home".
 * Returns the most recently saved match (which sits at index 0 since
 * `saveSavedPlace` unshifts).
 */
export function findHomePlace(): SavedPlace | undefined {
  for (const p of read()) {
    if (/(^|\s)home(\s|$)|house|apartment|apt/i.test(p.label)) return p;
  }
  return undefined;
}


/** Pick a sensible default icon from the label ("Home" → 🏠, "Work" → 💼, …). */
export function iconFor(label: string): string {
  const l = label.trim().toLowerCase();
  if (/(^|\s)home(\s|$)|house|apartment|apt/.test(l)) return "🏠";
  if (/work|office|job/.test(l)) return "💼";
  if (/gym|fitness|yoga/.test(l)) return "🏋️";
  if (/school|college|university|campus/.test(l)) return "🎓";
  if (/cafe|coffee/.test(l)) return "☕";
  if (/mom|dad|parents|family|grandma|grandpa|sister|brother/.test(l)) return "❤️";
  return "📍";
}
